import * as React from 'react'
import produce from 'immer'
import copyToClipboard from 'copy-to-clipboard'
import styled from 'styled-components'
import { Form, Spin, Tooltip, Button, Input, Tabs, Radio, Dropdown, Menu, message } from 'antd'
import { get, set, range, isEmpty, orderBy, debounce, cloneDeep, isEqual, trim } from 'lodash'
import { strEqual, DEBOUNCE } from 'helpers/utils'
import { showError, resourceNotFound, ValidationError, showClientNotifications } from 'helpers/errors'
import {
  createLabelFactory,
  createFieldFactory,
  createHandleSubmit,
  createDropdownRender,
  isReadOnly,
} from 'helpers/formViews'
import taskTypes from 'options/tasks/taskTypes'
import httpVerbs from 'options/tasks/httpVerbs'
import httpContentTypes from 'options/tasks/httpContentTypes'
import Checkbox, { CheckboxGroup } from 'elements/Checkbox'
import {
  MONTHS,
  DAYS_OF_WEEK,
  FREQUENCIES,
  JOB_FREQUENCIES,
  RECURRING_FREQUENCIES,
  MONTH_MODES,
  WEEKS_OF_MONTH,
  EMAIL_SETTINGS_CHECKS,
  SKIP_NOTIFICATION_CHECKS,
} from 'options/tasks'
import { t } from 'helpers/i18n'
import { Emitter, stopEvent } from 'helpers/events'
import { FORM_CHANGE_EVENT } from 'options/events'
import linkTargets from 'options/linkTargets'
import Drawer from 'elements/Drawer'
import Select, { Option, getOptionProps } from 'elements/Select'
import InputNumber from 'elements/InputNumber'
import DatePicker from 'elements/DatePicker'
import Icon from 'elements/Icon'
import { Row, Col } from 'elements/Grid'
import isEmail from 'validator/lib/isEmail'

const HttpHeaders = styled.div`
  input {
    &::-webkit-input-placeholder {
      color: #bbb;
    }

    &::-moz-placeholder {
      color: #bbb;
    }

    &:-ms-input-placeholder {
      color: #bbb;
    }

    &:-moz-placeholder {
      color: #bbb;
    }
  }
`

const mapHttpHeaders = (httpHeaders = {}) => {
  const entries = Object.entries(httpHeaders).map(([name, value]) => ({
    name,
    value,
  }))

  return [...Array(5).keys()].map((each, index) => entries[index] ?? { name: '', value: '' })
}

const JobsFormView = linkTargets.jobRecord.formComponent

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmit = createHandleSubmit(this)
    this.searchOperators = debounce(this.searchOperators, DEBOUNCE)
  }

  componentDidMount() {
    this.fetchItem()

    Emitter.on(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  componentWillUnmount() {
    Emitter.off(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  handleFormChange = () => this.setState({ formHasChanged: true })

  transformSaveItemParams = ({ params }) => ({
    ...(params.frequency === 'Monthly' && isEmpty(params.recurOnMonths) ? { recurOnMonths: range(1, 13) } : {}),
    ...(params.taskType === taskTypes.ScheduledReport
      ? EMAIL_SETTINGS_CHECKS.reduce((acc, each) => ({ ...acc, [each]: (params?.includes ?? []).includes(each) }), {})
      : {}),
    ...(this.props.settingsType === 'asset' ? { scheduleDescription: '?' } : {}),
    activeDescription: params.active ? t('yes') : t('no'),
    taskId: params.id,
    includes: undefined,
    sendToEmails: [...new Set(params.sendToEmails)],
    httpHeaders: params.httpHeadersMap
      .filter((each) => trim(each.name))
      .reduce((acc, each) => ({ ...acc, [trim(each.name)]: trim(each.value) }), {}),
    httpHeadersMap: undefined,
  })

  getItemId = () =>
    [this.state.item?.id, this.props.linkTargetRecord?.taskId, this.props.linkTargetRecord?.id].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    try {
      const responses = await Promise.all([
        itemId
          ? this.props.getItem(itemId).catch((error) => {
              this.props.onCancel?.()
              throw error
            })
          : !this.props.linkTargetRecord
            ? this.props.newItem({
                active: true,
                taskType: this.props.taskType,
                frequency: 'Once',
                name: this.props.name,
                filterTemplate: {
                  filterTemplateType: this.props.filterTemplateType,
                  id: this.props.filterTemplateId,
                },
                jobTaskSettings: { assignedOperatorId: 0, ...this.props.jobTaskSettings },
                recurOnMonths: [],
                recurOnDaysOfMonth: [],
                recurOnDaysOfWeek: [],
                recurOnWeeksOfMonth: [],
                sendToEmails:
                  this.props.settingsType === 'reports' && this.props.user.email ? [this.props.user.email] : [],
                monthMode: MONTH_MODES.MonthDay,
                startDate: new Date().toJSON(),
                recurPeriods: 1,
                owner: this.props.user.userName,
              })
            : resourceNotFound(this),
        this.props.getSettings({ type: this.props.settingsType }),
      ])

      const item = cloneDeep(responses[0].value.data)

      const assignedOperatorId = item?.jobTaskSettings?.assignedOperatorId
      const isScheduledReport = item.taskType === taskTypes.ScheduledReport
      const isScheduledJob = item.taskType === taskTypes.ScheduledJob

      if (item.taskType === taskTypes.ScheduledReport) {
        if (!item.id) {
          item.includeHtmlInEmail = true
        }

        item.includes = []

        EMAIL_SETTINGS_CHECKS.forEach((one) => {
          if (item[one]) {
            item.includes.push(one)
          }
        })
      }

      if (item.taskType === taskTypes.ScheduledJob && this.props.settingsType === 'asset') {
        set(item, 'jobTaskSettings.assetId', this.props.parentRecord?.id)
        set(item, 'jobTaskSettings.assetBarcode', this.props.parentRecord?.barcode)
        set(item, 'jobTaskSettings.assetLocationId', this.props.parentRecord?.locationId)
      }

      const [filterTemplates, filterTemplateTypes, jobTemplates, operators] = await Promise.all([
        isScheduledReport && item.filterTemplate.filterTemplateType
          ? this.props.getFilterTemplates({
              filterTemplateType: item.filterTemplate.filterTemplateType,
              owner: item.owner,
            })
          : Promise.resolve(null),
        isScheduledReport ? this.props.getFilterTemplateTypes() : Promise.resolve(null),
        isScheduledJob ? this.props.getJobTemplates() : Promise.resolve(null),
        isScheduledJob
          ? this.props.getOperators(assignedOperatorId ? { operatorIds: [assignedOperatorId] } : null)
          : Promise.resolve(null),
      ])

      item.httpHeadersMap = mapHttpHeaders(item.httpHeaders)

      this.setState(
        {
          item,
          itemOriginal: cloneDeep(item),
          fieldSettings: responses[1].value.data.fieldSettings,
          filterTemplates: get(filterTemplates, 'value.data.items', []),
          filterTemplateTypes: get(filterTemplateTypes, 'value.data.items', []),
          jobTemplates: get(jobTemplates, 'value.data.items', []),
          operators: get(operators, 'value.data', {}),
          formHasChanged: false,
        },
        this.fetchAssignedToFromTemplate
      )
    } catch (error) {
      showError({ error })
    }
  }

  saveItem = async (item) => {
    const {
      createItem = (data) => Promise.resolve({ value: { data } }),
      updateItem = (data) => Promise.resolve({ value: { data } }),
    } = this.props

    if (!item) {
      throw new Error('item is undefined')
    }

    const saved = item.id ? await updateItem(item) : await createItem(item)

    return saved
  }

  setItemValue = (name, value) => {
    const { item } = this.state

    if (name === 'frequency' && value === 'Yearly') {
      this.setItemValue('recurOnMonths', [1])
      this.setItemValue('recurOnDaysOfMonth', [1])
    }

    if (name === 'frequency' && value === 'Monthly') {
      this.setItemValue('recurOnMonths', [])
      this.setItemValue('recurOnDaysOfMonth', [])
    }

    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      }),
      async () => {
        if (name === 'monthMode') {
          ;['recurOnDaysOfWeek', 'recurOnDaysOfMonth', 'recurOnWeeksOfMonth'].forEach((fieldName) =>
            this.props.form.setFields({
              [fieldName]: {
                value: this.props.form.getFieldValue(fieldName),
                errors: null,
              },
            })
          )
        }

        if (name === 'jobTaskSettings.templateId') {
          this.setJobTemplateName()
          this.fetchAssignedToFromTemplate()
        }

        if (name === 'filterTemplate.filterTemplateType') {
          try {
            const filterTemplates = await this.props.getFilterTemplates({
              filterTemplateType: value,
              owner: item.owner,
            })

            this.setState({ filterTemplates: filterTemplates.value.data.items }, () =>
              this.setItemValue('filterTemplate', {})
            )
          } catch (error) {
            showError({ error })
          }
        }
      }
    )
  }

  setJobTemplateName = () => {
    const { item, jobTemplates } = this.state
    const jobTemplateId = item?.jobTaskSettings?.templateId

    this.setItemValue('jobTaskSettings.jobTemplateName', jobTemplates.find((one) => one.id === jobTemplateId).name)
  }

  searchOperators = async (search) => {
    try {
      const { item } = this.state
      const assignedOperatorId = item?.jobTaskSettings?.assignedOperatorId
      const response = await this.props.getOperators({
        search,
        operatorIds: assignedOperatorId ? [assignedOperatorId] : undefined,
      })

      this.setState({ operators: response.value.data })
    } catch (error) {
      showError({ error })
    }
  }

  fetchAssignedToFromTemplate = async () => {
    const { item, jobTemplates } = this.state
    const isScheduledJob = item.taskType === taskTypes.ScheduledJob
    const assignedOperatorId = item?.jobTaskSettings?.assignedOperatorId

    if (!(isScheduledJob && !assignedOperatorId)) {
      this.setState({ assignedToFromTemplate: null })
      return
    }

    const jobTemplateId = item?.jobTaskSettings?.templateId
    const jobTemplate = jobTemplates.find((one) => one.id === jobTemplateId)
    const assignedToFromTemplateId = jobTemplate?.assignedToId

    if (!assignedToFromTemplateId) {
      this.setState({ assignedToFromTemplate: null })
      return
    }

    try {
      const response = await this.props.getOperator(assignedToFromTemplateId)

      this.setState({ assignedToFromTemplate: response.value.data })
    } catch (error) {
      showError({ error })
      this.setState({ assignedToFromTemplate: null })
    }
  }

  getAssignedOperatorIdExtra = () => {
    const { item, assignedToFromTemplate, operators } = this.state
    const assignedOperatorId = item?.jobTaskSettings?.assignedOperatorId
    let assignedTo

    if (assignedOperatorId) {
      assignedTo = operators.items.find((one) => one.id === assignedOperatorId)
    } else if (assignedToFromTemplate) {
      assignedTo = assignedToFromTemplate
    }

    if (assignedTo) {
      return `${t('assignedToTaskInfo')} ${assignedTo.name} ${assignedTo.email ? `(${assignedTo.email})` : ''}`
    }

    return t('noAssignedToTaskInfo')
  }

  handleActionsMenuClick = async ({ key }) => {
    const { item } = this.state

    if (key !== 'runNow') {
      message.warn(t('underDevelopment'))
      return
    }

    if (this.hasUnsavedChanges()) {
      message.error(t('saveChangesFirst'))
      return
    }

    if (item.taskType === taskTypes.ScheduledReport) {
      try {
        await this.props.onSave?.(true)

        const response = await this.props.runNow(item.id)

        showClientNotifications({ response })
      } catch (error) {
        showError({ error })
      } finally {
        await this.props.onSave?.(false)
      }
    }

    if (item.taskType === taskTypes.ScheduledJob) {
      try {
        await this.props.onSave?.(true)

        const job = await this.props
          .createFromTemplate(item.jobTaskSettings.templateId, item.jobTaskSettings)
          .then((r) => r.value.data)

        this.setState({
          createJobVisible: true,
          createJobTitle: `${t('job')} - ${job.name}`,
          createJobSaving: false,
          createJobLinkTargetRecord: { ...job, jobId: job.id },
        })
      } catch (error) {
        showError({ error })
      } finally {
        await this.props.onSave?.(false)
      }
    }
  }

  hasUnsavedChanges = () =>
    this.state.formHasChanged || !this.state.item?.id || !isEqual(this.state.item, this.state.itemOriginal)

  render() {
    const { item, fieldSettings, operators, filterTemplateTypes, filterTemplates = [] } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const readOnly =
      isReadOnly(this) ||
      (item.taskType === taskTypes.ScheduledReport && !strEqual(item.owner, this.props.user.userName))

    const sendToEmailsRequired = fieldSettings.find((one) => one.dtoFieldName === 'sendToEmails')?.isRequired
    const sendToEmailsDisabled = !SKIP_NOTIFICATION_CHECKS.some((one) => Boolean(this.props.form.getFieldValue(one)))

    return (
      <>
        <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
          {item.id ? (
            <Dropdown
              overlay={
                <Menu onClick={this.handleActionsMenuClick}>
                  <Menu.Item key="runNow">{t('runNow')}</Menu.Item>
                </Menu>
              }
              trigger={['click']}
            >
              <Button className="form-items-actions">
                {t('actions')}
                <Icon type="KeyboardArrowDown" />
              </Button>
            </Dropdown>
          ) : null}
          <div className="form-items-container">
            <ValidationError errors={this.state.validationErrors} />
            <Row>
              <Col half>
                <Form.Item label={createLabel('name')}>
                  {createFieldDecorator('name')(<Input readOnly={readOnly} autoFocus />)}
                </Form.Item>
                {item.taskType === taskTypes.ScheduledJob && (
                  <Form.Item label={createLabel('taskType')}>
                    <Input value={t('scheduledJob')} disabled />
                  </Form.Item>
                )}
                {item.taskType === taskTypes.ScheduledReport && (
                  <Form.Item label={createLabel('filterTemplate.filterTemplateType')}>
                    {createFieldDecorator('filterTemplate.filterTemplateType')(
                      <Select
                        onChange={(value) => this.setItemValue('filterTemplate.filterTemplateType', value)}
                        readOnly={readOnly}
                      >
                        {orderBy(filterTemplateTypes, [(each) => t(each.displayLanguageKey)]).map((each) => (
                          <Option key={each.filterTemplateType} value={each.filterTemplateType}>
                            {t(each.displayLanguageKey)}
                          </Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                )}
              </Col>
              <Col half>
                <Form.Item label={createLabel('lastRun')}>
                  {createFieldDecorator('lastRun')(<DatePicker placeholder={t('never')} showTime disabled />)}
                </Form.Item>
                <Form.Item label={createLabel('nextRun')}>
                  {createFieldDecorator('nextRun')(<DatePicker placeholder={t('never')} showTime disabled />)}
                </Form.Item>
              </Col>
            </Row>
            <Tabs
              activeKey={this.state.tabsActiveKey || 'settings'}
              onChange={(tabsActiveKey) => this.setState({ tabsActiveKey })}
            >
              <Tabs.TabPane key="settings" tab={t('settings')} forceRender>
                <Row>
                  <Col half>
                    {item.taskType === taskTypes.ScheduledReport && (
                      <Form.Item label={createLabel('filterTemplate.id')}>
                        {createFieldDecorator('filterTemplate.id')(
                          <Select
                            onChange={(value) =>
                              this.setItemValue(
                                'filterTemplate',
                                filterTemplates.find((one) => one.id === value)
                              )
                            }
                            readOnly={readOnly}
                            disabled={isEmpty(this.props.form.getFieldValue('filterTemplate.filterTemplateType'))}
                            allowClear
                          >
                            {filterTemplates
                              .filter((each) => each.name)
                              .map((each) => (
                                <Option key={each.id} value={each.id}>
                                  <span {...getOptionProps(each)}>{each.name}</span>
                                </Option>
                              ))}
                          </Select>
                        )}
                      </Form.Item>
                    )}
                    {item.taskType === taskTypes.ScheduledJob && (
                      <Form.Item label={createLabel('jobTaskSettings.templateId')}>
                        {createFieldDecorator('jobTaskSettings.templateId')(
                          <Select
                            onChange={(value) => this.setItemValue('jobTaskSettings.templateId', value)}
                            allowClear={false}
                            readOnly={readOnly}
                          >
                            {(this.state.jobTemplates ?? []).map((each) => (
                              <Option key={each.id} value={each.id}>
                                <span {...getOptionProps(each)}>{each.displayName}</span>
                              </Option>
                            ))}
                          </Select>
                        )}
                      </Form.Item>
                    )}
                  </Col>
                  <Col half>
                    <Form.Item className="mt-24">
                      {createFieldDecorator('active', { valuePropName: 'checked' })(
                        <Checkbox readOnly={readOnly}>{createLabel('active')}</Checkbox>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                {item.taskType === taskTypes.ScheduledJob && (
                  <Row>
                    <Col half>
                      {item?.jobTaskSettings?.templateId && (
                        <Form.Item
                          label={createLabel('jobTaskSettings.assignedOperatorId')}
                          extra={this.getAssignedOperatorIdExtra()}
                        >
                          {createFieldDecorator('jobTaskSettings.assignedOperatorId')(
                            <Select
                              onChange={(value) => this.setItemValue('jobTaskSettings.assignedOperatorId', value || 0)}
                              placeholder={t('select')}
                              dropdownRender={createDropdownRender(operators)}
                              onSearch={this.searchOperators}
                              readOnly={readOnly}
                              showSearch
                            >
                              <Option value={0}>{t('fromTemplate')}</Option>
                              {(operators?.items ?? [])
                                .filter((each) => each.id)
                                .map((each) => (
                                  <Option key={each.id} value={each.id}>
                                    <span {...getOptionProps(each)}>{each.displayName}</span>
                                  </Option>
                                ))}
                            </Select>
                          )}
                        </Form.Item>
                      )}
                      {SKIP_NOTIFICATION_CHECKS.map((each) => (
                        <Form.Item key={each} className="mb-12">
                          {createFieldDecorator(each, { valuePropName: 'checked' })(
                            <Checkbox readOnly={readOnly}>{createLabel(each)}</Checkbox>
                          )}
                        </Form.Item>
                      ))}
                      <Form.Item label={createLabel('sendToEmails')}>
                        <div className="tofino-label-buttons">
                          <Tooltip title={t('copyToClipboard')}>
                            <Button
                              size="small"
                              onClick={() => {
                                copyToClipboard(item.sendToEmails.join(' '), {
                                  message: t('copyToClipboard'),
                                  format: 'text/plain',
                                })
                              }}
                              disabled={sendToEmailsDisabled}
                            >
                              <Icon type="FileCopy" />
                            </Button>
                          </Tooltip>
                        </div>
                        {this.props.form.getFieldDecorator('sendToEmails', {
                          rules: [
                            {
                              required: sendToEmailsRequired,
                              message: t('requiredField'),
                            },
                          ],
                          initialValue: [...new Set(item?.sendToEmails ?? [])],
                        })(
                          <Select
                            mode="tags"
                            tokenSeparators={[',', ' ', ';']}
                            maxTagCount={100}
                            onChange={(value) =>
                              this.setItemValue(
                                'sendToEmails',
                                value.filter((each) => isEmail(each))
                              )
                            }
                            readOnly={readOnly}
                            disabled={sendToEmailsDisabled}
                          />
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                )}
              </Tabs.TabPane>
              {item.taskType === taskTypes.ScheduledReport && (
                <Tabs.TabPane key="email" tab={t('email')} forceRender>
                  <Row>
                    <Col half>
                      <Form.Item label={createLabel('sendToEmails')}>
                        <div className="tofino-label-buttons">
                          <Tooltip title={t('copyToClipboard')}>
                            <Button
                              size="small"
                              onClick={() => {
                                copyToClipboard((item?.sendToEmails ?? []).join(' '), {
                                  message: t('copyToClipboard'),
                                  format: 'text/plain',
                                })
                              }}
                            >
                              <Icon type="FileCopy" />
                            </Button>
                          </Tooltip>
                        </div>
                        {this.props.form.getFieldDecorator('sendToEmails', {
                          rules: [
                            {
                              required: sendToEmailsRequired,
                              message: t('requiredField'),
                            },
                          ],
                          initialValue: [...new Set(item?.sendToEmails ?? [])],
                        })(
                          <Select
                            mode="tags"
                            tokenSeparators={[',', ' ', ';']}
                            maxTagCount={100}
                            onChange={(value) =>
                              this.setItemValue(
                                'sendToEmails',
                                value.filter((each) => isEmail(each))
                              )
                            }
                            readOnly={readOnly}
                          />
                        )}
                      </Form.Item>
                      <Form.Item>
                        {this.props.form.getFieldDecorator('includes', {
                          rules: [
                            {
                              required: true,
                              message: t('errorNoContent'),
                            },
                          ],
                          initialValue: item?.includes ?? [],
                        })(
                          <CheckboxGroup disabled={readOnly}>
                            {EMAIL_SETTINGS_CHECKS.map((each) => (
                              <div key={each} className="mb-12">
                                <Checkbox value={each}>{createLabel(each)}</Checkbox>
                              </div>
                            ))}
                          </CheckboxGroup>
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                </Tabs.TabPane>
              )}
              {item.taskType === taskTypes.ScheduledReport && (
                <Tabs.TabPane key="http" tab={t('http')} forceRender>
                  <Row>
                    <Col half>
                      <Form.Item>
                        <Checkbox
                          onChange={(e) => this.setItemValue('sendOverHttp', e.target.checked)}
                          checked={item.sendOverHttp}
                          readOnly={readOnly}
                        >
                          {createLabel('sendOverHttp')}
                        </Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>
                  {item.sendOverHttp && (
                    <>
                      <Form.Item label={createLabel('sendToUrl')}>
                        {createFieldDecorator('sendToUrl')(<Input type="url" readOnly={readOnly} />)}
                      </Form.Item>
                      <Row>
                        <Col half>
                          <Form.Item label={createLabel('httpVerb')}>
                            {createFieldDecorator('httpVerb')(
                              <Select
                                placeholder={t('select')}
                                onChange={(value) => this.setItemValue('httpVerb', value)}
                                readOnly={readOnly}
                                allowClear={false}
                              >
                                {Object.entries(httpVerbs).map(([key, value]) => (
                                  <Option key={key} value={key}>
                                    {value}
                                  </Option>
                                ))}
                              </Select>
                            )}
                          </Form.Item>
                        </Col>
                        <Col half>
                          <Form.Item label={createLabel('httpContentType')}>
                            {createFieldDecorator('httpContentType')(
                              <Select
                                placeholder={t('select')}
                                onChange={(value) => this.setItemValue('httpContentType', value)}
                                readOnly={readOnly}
                                allowClear={false}
                              >
                                {Object.entries(httpContentTypes).map(([key, value]) => (
                                  <Option key={key} value={key}>
                                    {value}
                                  </Option>
                                ))}
                              </Select>
                            )}
                          </Form.Item>
                        </Col>
                      </Row>
                      <HttpHeaders>
                        <Form.Item label={t('httpHeaders')}>
                          {mapHttpHeaders(item.httpHeaders).map((each, i) => (
                            <Row key={`httpHeaders${i}`} gutter={12}>
                              <Col xs={12}>
                                <div className="whitespace-nowrap mb-6">
                                  <Input
                                    onChange={(e) => this.setItemValue(`httpHeadersMap[${i}].name`, e.target.value)}
                                    placeholder={t('name')}
                                    value={get(item, `httpHeadersMap[${i}].name`)}
                                    readOnly={readOnly}
                                  />
                                </div>
                              </Col>
                              <Col xs={12}>
                                <div className="whitespace-nowrap mb-6">
                                  <Input
                                    onChange={(e) => this.setItemValue(`httpHeadersMap[${i}].value`, e.target.value)}
                                    placeholder={t('value')}
                                    value={get(item, `httpHeadersMap[${i}].value`)}
                                    readOnly={readOnly}
                                  />
                                </div>
                              </Col>
                            </Row>
                          ))}
                        </Form.Item>
                      </HttpHeaders>
                    </>
                  )}
                </Tabs.TabPane>
              )}
              {item.taskType === taskTypes.ScheduledReport && (
                <Tabs.TabPane key="ftp" tab={t('ftp')} forceRender>
                  <Row>
                    <Col half>
                      <Form.Item>
                        <Checkbox
                          onChange={(e) => this.setItemValue('sendOverFtp', e.target.checked)}
                          checked={item.sendOverFtp}
                          readOnly={readOnly}
                        >
                          {createLabel('sendOverFtp')}
                        </Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>
                  {item.sendOverFtp && (
                    <>
                      <Form.Item label={createLabel('sendToFtpUrl')}>
                        {createFieldDecorator('sendToFtpUrl')(<Input type="url" readOnly={readOnly} />)}
                      </Form.Item>
                      <Form.Item label={createLabel('sendToFtpPort')}>
                        {createFieldDecorator('sendToFtpPort')(<InputNumber readOnly={readOnly} />)}
                      </Form.Item>
                      <Form.Item label={createLabel('sendToFtpUser')}>
                        {createFieldDecorator('sendToFtpUser')(<Input readOnly={readOnly} />)}
                      </Form.Item>
                      <Form.Item label={createLabel('sendToFtpPassword')}>
                        {createFieldDecorator('sendToFtpPassword')(
                          <Input.Password type="password" readOnly={readOnly} />
                        )}
                      </Form.Item>
                      <Form.Item label={createLabel('sendToFtpDirectory')}>
                        {createFieldDecorator('sendToFtpDirectory')(<Input readOnly={readOnly} />)}
                      </Form.Item>
                    </>
                  )}
                </Tabs.TabPane>
              )}
              <Tabs.TabPane key="schedule" tab={t('schedule')} forceRender>
                <Row>
                  <Col half>
                    <Form.Item label={createLabel('frequency')}>
                      {createFieldDecorator('frequency')(
                        <Select
                          onChange={(value) => this.setItemValue('frequency', value)}
                          allowClear={false}
                          readOnly={readOnly}
                        >
                          {Object.entries(FREQUENCIES)
                            .filter(
                              ([key, _]) => item.taskType === taskTypes.ScheduledJob || !JOB_FREQUENCIES.includes(key)
                            )
                            .map(([key, value]) => (
                              <Option key={key} value={key}>
                                {t(value)}
                              </Option>
                            ))}
                        </Select>
                      )}
                    </Form.Item>
                    {!JOB_FREQUENCIES.includes(item.frequency) && (
                      <Form.Item
                        label={item.frequency !== 'Once' ? createLabel('startDate') : t('startingFromOnceLabel')}
                      >
                        {createFieldDecorator('startDate')(
                          <DatePicker
                            placeholder={t('select')}
                            setTimeMode="exact"
                            allowClear={false}
                            readOnly={readOnly}
                            showTime
                          />
                        )}
                      </Form.Item>
                    )}
                    {Object.keys(RECURRING_FREQUENCIES).includes(item.frequency) && (
                      <Form.Item label={t(RECURRING_FREQUENCIES[item.frequency])}>
                        {createFieldDecorator('recurPeriods')(<InputNumber min={1} readOnly={readOnly} />)}
                      </Form.Item>
                    )}
                    {item.frequency === 'Weekly' && (
                      <Form.Item label={createLabel('recurOnDaysOfWeek')}>
                        {createFieldDecorator('recurOnDaysOfWeek')(
                          <Select
                            placeholder={t('select')}
                            onChange={(value) => this.setItemValue('recurOnDaysOfWeek', value)}
                            mode="multiple"
                            maxTagCount={7}
                            readOnly={readOnly}
                          >
                            {DAYS_OF_WEEK.map((each, index) => (
                              <Option key={each} value={index}>
                                {t(each)}
                              </Option>
                            ))}
                          </Select>
                        )}
                      </Form.Item>
                    )}
                    {item.frequency === 'Monthly' && (
                      <>
                        <Form.Item label={createLabel('recurOnMonths')}>
                          <Select
                            value={get(item, 'recurOnMonths.length', 0) < 12 ? item.recurOnMonths : []}
                            placeholder={t('all')}
                            onChange={(value) => this.setItemValue('recurOnMonths', value)}
                            mode="multiple"
                            maxTagCount={12}
                            readOnly={readOnly}
                          >
                            {Object.keys(MONTHS).map((each, index) => (
                              <Option key={each} value={index + 1}>
                                {t(each)}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Row gutter={24} className="mb-6">
                          <Col xs={3}>
                            <Radio
                              checked={item.monthMode === MONTH_MODES.MonthDay}
                              onChange={() => !readOnly && this.setItemValue('monthMode', MONTH_MODES.MonthDay)}
                            />
                          </Col>
                          <Col xs={21}>
                            <Row className="mb-6">
                              <Col>
                                <Form.Item label={t('monthDay')} disabled={item.monthMode !== MONTH_MODES.MonthDay}>
                                  {this.props.form.getFieldDecorator('recurOnDaysOfMonth', {
                                    rules: [
                                      {
                                        required: item.monthMode === MONTH_MODES.MonthDay,
                                        message: t('requiredField'),
                                      },
                                    ],
                                    initialValue: item.recurOnDaysOfMonth,
                                  })(
                                    <Select
                                      placeholder={t('select')}
                                      onChange={(value) => this.setItemValue('recurOnDaysOfMonth', value)}
                                      mode="multiple"
                                      maxTagCount={31}
                                      disabled={item.monthMode !== MONTH_MODES.MonthDay}
                                      readOnly={readOnly}
                                      allowClear
                                    >
                                      {range(1, 32).map((each, index) => (
                                        <Option key={each} value={each}>
                                          {each}
                                        </Option>
                                      ))}
                                      <Option key={32} value={32}>
                                        {t('last')}
                                      </Option>
                                    </Select>
                                  )}
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                        <Row gutter={24} className="mb-6">
                          <Col xs={3}>
                            <Radio
                              checked={item.monthMode === MONTH_MODES.Weekday}
                              onChange={() => !readOnly && this.setItemValue('monthMode', MONTH_MODES.Weekday)}
                            />
                          </Col>
                          <Col xs={21}>
                            <Row className="mb-6">
                              <Col>
                                <Form.Item label={t('weekday')} disabled={item.monthMode !== MONTH_MODES.Weekday}>
                                  {this.props.form.getFieldDecorator('recurOnWeeksOfMonth', {
                                    rules: [
                                      {
                                        required: item.monthMode === MONTH_MODES.Weekday,
                                        message: t('requiredField'),
                                      },
                                    ],
                                    initialValue: item.recurOnWeeksOfMonth,
                                  })(
                                    <Select
                                      placeholder={t('select')}
                                      onChange={(value) => this.setItemValue('recurOnWeeksOfMonth', value)}
                                      mode="multiple"
                                      maxTagCount={6}
                                      disabled={item.monthMode !== MONTH_MODES.Weekday}
                                      readOnly={readOnly}
                                      allowClear
                                    >
                                      {[...Array(5).keys()].map((each, index) => (
                                        <Option key={each} value={each + 1}>
                                          {t(WEEKS_OF_MONTH[each])}
                                        </Option>
                                      ))}
                                    </Select>
                                  )}
                                </Form.Item>
                                <Form.Item disabled={item.monthMode !== MONTH_MODES.Weekday}>
                                  {this.props.form.getFieldDecorator('recurOnDaysOfWeek', {
                                    rules: [
                                      {
                                        required: item.monthMode === MONTH_MODES.Weekday,
                                        message: t('requiredField'),
                                      },
                                    ],
                                    initialValue: item.recurOnDaysOfWeek,
                                  })(
                                    <Select
                                      placeholder={t('select')}
                                      onChange={(value) => this.setItemValue('recurOnDaysOfWeek', value)}
                                      mode="multiple"
                                      maxTagCount={7}
                                      disabled={item.monthMode !== MONTH_MODES.Weekday}
                                      readOnly={readOnly}
                                      allowClear
                                    >
                                      {DAYS_OF_WEEK.map((each, index) => (
                                        <Option key={each} value={index}>
                                          {t(each)}
                                        </Option>
                                      ))}
                                    </Select>
                                  )}
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </>
                    )}
                    {item.frequency === 'Yearly' && (
                      <Form.Item label={t('day')}>
                        <Row gutter={6}>
                          <Col xs={12}>
                            <Select
                              onChange={(value) => {
                                this.setItemValue('recurOnMonths', [value])
                                this.setItemValue('recurOnDaysOfMonth', [1])
                              }}
                              value={get(item, 'recurOnMonths[0]', 1)}
                              allowClear={false}
                              readOnly={readOnly}
                            >
                              {Object.keys(MONTHS).map((each, index) => (
                                <Option key={each} value={index + 1}>
                                  {t(each)}
                                </Option>
                              ))}
                            </Select>
                          </Col>
                          <Col xs={12}>
                            <Select
                              placeholder={t('select')}
                              onChange={(value) => this.setItemValue('recurOnDaysOfMonth', [value])}
                              value={get(item, 'recurOnDaysOfMonth[0]', 1)}
                              allowClear={false}
                              readOnly={readOnly}
                            >
                              {range(1, Object.values(MONTHS)[get(item, 'recurOnMonths[0]', 1) - 1] + 1).map(
                                (each, index) => (
                                  <Option key={each} value={each}>
                                    {each}
                                  </Option>
                                )
                              )}
                            </Select>
                          </Col>
                        </Row>
                      </Form.Item>
                    )}
                    {JOB_FREQUENCIES.includes(item.frequency) && <p>{t('enterScheduleValue')}</p>}
                    {JOB_FREQUENCIES.includes(item.frequency) && (
                      <Row>
                        <Col xs={12}>
                          <Form.Item label={t(item.frequency)}>
                            {createFieldDecorator('recurPeriods')(<InputNumber min={1} readOnly={readOnly} />)}
                          </Form.Item>
                        </Col>
                        <Col xs={12}>
                          <Form.Item className="mt-24">
                            {createFieldDecorator('repeatForRecurPeriod', {
                              valuePropName: 'checked',
                            })(<Checkbox readOnly={readOnly}>{createLabel('repeatForRecurPeriod')}</Checkbox>)}
                          </Form.Item>
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>
              </Tabs.TabPane>
            </Tabs>
          </div>
          <div className="form-buttons-container">
            {readOnly ? (
              <Button onClick={this.props.onCancel}>{t('close')}</Button>
            ) : (
              <>
                <Tooltip title={t('unsavedChangesWillBeLost')}>
                  <Button onClick={this.props.onCancel}>{t('cancel')}</Button>
                </Tooltip>{' '}
                {this.props.settingsType !== 'asset' && (
                  <Button
                    onClick={(e) => this.handleSubmit(e, { saveAndClose: false })}
                    loading={this.state.saveButtonLoading}
                  >
                    {t('save')}
                  </Button>
                )}{' '}
                <Button type="primary" htmlType="submit" loading={this.state.saveAndCloseButtonLoading}>
                  {t(this.props.settingsType !== 'asset' ? 'saveAndClose' : 'save')}
                </Button>
              </>
            )}
          </div>
        </Form>
        <Drawer
          title={this.state.createJobTitle}
          size={linkTargets.jobRecord.formSize}
          visible={this.state.createJobVisible}
          onClose={() => this.setState({ createJobVisible: false })}
          saving={this.state.createJobSaving}
        >
          <JobsFormView
            onSave={(pending) => this.setState({ createJobSaving: pending })}
            onSaveAndClose={() => this.setState({ createJobVisible: false })}
            onCancel={() => this.setState({ createJobVisible: false })}
            linkTargetRecord={this.state.createJobLinkTargetRecord}
          />
        </Drawer>
      </>
    )
  }
}

export default Form.create({
  onValuesChange: (...params) => Emitter.emit(FORM_CHANGE_EVENT, ...params),
})(FormView)
